import { createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, unref as _unref, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = {
  class: "flex-1 overflow-auto px-6 py-1",
  style: {
    "max-height": "80vh",
    "outline": "none"
  }
};
const _hoisted_2 = {
  class: "p-5 border-t border-gray-200 bg-white sticky bottom-0 z-10 mr-16"
};
const _hoisted_3 = {
  class: "flex items-center justify-between"
};
const _hoisted_4 = {
  class: "flex items-center gap-2"
};
import { ref, onMounted, reactive, computed } from 'vue';
import { useChargesStore } from '@/services/UserService/useChargesStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { useMatterChangeDetection } from '@/composables/useMatterChangeDetection';
import EventTracker from '@/services/eventTracker';
import { TrackingCategory, TrackingAction } from '@/constants/trackingConstants';
import CreateChargeForm from './CreateChargeForm.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlConfirmationDialog from '@/components/LlConfirmationDialog.vue';
import DeleteRecurringFeesDialog from './DeleteRecurringFeesDialog.vue';
export default {
  __name: 'CreateChargePanel',
  props: {
    state: Object,
    stateParams: Object,
    rootScope: {
      type: Object,
      required: false
    },
    alerts: {
      type: Object,
      required: false
    },
    snippetService: {
      type: Object,
      required: false
    },
    entryEditService: {
      type: Object,
      required: false
    },
    tracking: {
      type: Object,
      required: false
    },
    entryPoint: {
      type: String,
      required: false,
      default: 'fixed_fee_list'
    },
    clientMatterManager: {
      type: Object,
      required: false
    }
  },
  emits: ['close', 'saved', 'savedAndContinue', 'chargeLoaded'],
  setup(__props, {
    expose: __expose,
    emit: __emit
  }) {
    const emit = __emit;
    const props = __props;
    const chargesStore = useChargesStore();
    const {
      handleError,
      handleSuccess
    } = useSnackbarHandler();
    const {
      hasMatterChanged,
      handleMatterChange,
      getMatterChangeMessage,
      calculateAmountChange
    } = useMatterChangeDetection();
    const panelRef = ref(null);
    const formRef = ref(null);
    const matterChangeConfirmationRef = ref(null);
    const deleteConfirmationRef = ref(null);
    const deleteRecurringRef = ref(null);
    const state = reactive({
      chargeId: null,
      originalMatterId: null,
      isBilled: false,
      isSaving: false,
      isDeleting: false
    });
    const {
      fixedFeeTemplate
    } = props.stateParams;
    const matterChangeMessage = computed(() => getMatterChangeMessage());
    const handleChargeLoaded = chargeData => {
      state.chargeId = chargeData.id || null;
      state.isBilled = chargeData.billed || false;
      if (chargeData.id && state.originalMatterId === null) {
        state.originalMatterId = chargeData.matter?.id || chargeData.cm?.matter?.id || null;
      }
      emit('chargeLoaded', chargeData);
    };
    const handleFixedFeeSaved = () => {
      emit('savedAndContinue');
    };

    /**
     * Shared logic for updating charge with matter change detection
     */
    const updateChargeWithMatterChange = async (chargeId, chargeData, originalAmount) => {
      const charge = formRef.value.getChargeData();
      const newFixedFeeAmount = calculateAmountChange(charge.amount, originalAmount);
      const newMatterId = chargeData.matter?.id || charge.cm?.matter?.id;
      await chargesStore.updateCharge(chargeId, chargeData, newFixedFeeAmount);
      if (hasMatterChanged(state.originalMatterId, newMatterId)) {
        const {
          newOriginalMatterId
        } = await handleMatterChange({
          chargeId,
          originalMatterId: state.originalMatterId,
          newMatterId,
          showConfirmation: () => matterChangeConfirmationRef.value.open()
        });
        if (newOriginalMatterId) {
          state.originalMatterId = newOriginalMatterId;
        }
      }
    };

    /**
     * Handle save and continue (from unsaved changes dialog)
     */
    const handleSaveAndContinue = async ({
      chargeData,
      chargeId,
      originalAmount
    }) => {
      try {
        if (chargeId) {
          await updateChargeWithMatterChange(chargeId, chargeData, originalAmount);
        } else {
          await chargesStore.createChargeWithType(chargeData, fixedFeeTemplate);
        }
        return true;
      } catch (error) {
        handleError(error);
        return false;
      }
    };
    const submitCharge = async chargeData => {
      state.isSaving = true;
      try {
        if (state.chargeId) {
          await updateCharge(state.chargeId, chargeData);
        } else {
          await createCharge(chargeData);

          // Track Fixed Fee Created event
          if (props.tracking) {
            EventTracker.trackEvent(props.tracking, TrackingCategory.FIXED_FEE, TrackingAction.CREATED, {
              entryPoint: props.entryPoint,
              isTemplate: !!fixedFeeTemplate
            });
          }
        }
        handleSuccess(`${fixedFeeTemplate ? 'Fixed fee template' : 'Fixed fee'} ${state.chargeId ? 'updated' : 'created'} successfully`);
        emit('saved');
      } catch (error) {
        handleError(error);
      } finally {
        state.isSaving = false;
      }
    };
    const saveCharge = async () => {
      if (!formRef.value) return;
      const allocationValid = formRef.value.getAllocationValid();
      if (!fixedFeeTemplate && !allocationValid) {
        handleError('Allocation amounts must total the fixed fee amount');
        return;
      }
      const validationErrors = formRef.value.validateForm();
      if (validationErrors.length) {
        validationErrors.forEach(error => handleError(error));
        return;
      }
      const chargeData = formRef.value.prepareData();
      if (!chargeData) {
        return;
      }
      await submitCharge(chargeData);
    };
    const deleteCharge = async () => {
      if (!formRef.value) return;
      try {
        const charge = formRef.value.getChargeData();

        // If this is a child instance of a recurring series, show the recurring delete dialog
        if (charge.isRecurringSeries && !charge.isParentRecurringCharge) {
          const deleteOption = await deleteRecurringRef.value?.open();
          if (!deleteOption) return; // User cancelled

          const mode = deleteOption === 'future' ? 'ThisAndFutureInstances' : 'ThisInstance';
          state.isDeleting = true;
          await chargesStore.deleteRecurring(charge.id, mode, true);
          handleSuccess(deleteOption === 'future' ? 'Recurring fees deleted successfully' : 'Fixed fee deleted successfully');
        } else {
          const confirmed = await deleteConfirmationRef.value?.open();
          if (!confirmed) return;
          state.isDeleting = true;
          await chargesStore.deleteCharge(charge.id);
          handleSuccess(`${fixedFeeTemplate ? 'Fixed fee template' : 'Fixed fee'} deleted successfully`);
        }
        emit('saved');
      } catch (error) {
        handleError(error);
      } finally {
        state.isDeleting = false;
      }
    };
    const closePanel = async () => {
      // Just emit close - the parent (FixedFeeEditPanel) handles the unsaved changes check
      // This prevents the confirmation dialog from showing twice
      emit('close');
    };
    const createCharge = async chargeData => {
      return chargesStore.createChargeWithType(chargeData, fixedFeeTemplate);
    };
    const updateCharge = async (id, chargeData) => {
      const originalAmount = formRef.value.getOriginalAmount();
      await updateChargeWithMatterChange(id, chargeData, originalAmount);
    };
    const activate = async searchText => {
      if (formRef.value) {
        await formRef.value.activate(searchText);
      }
    };

    /**
     * Confirm whether to proceed when there are unsaved changes
     * Delegates to the form component's confirmation logic
     * @param {String} action - Description of the action being performed
     * @returns {Promise<Boolean>} - true if should proceed, false if should stay
     */
    const confirmDiscardChanges = async (action = 'selecting another fixed fee') => {
      if (formRef.value?.confirmDiscardChanges) {
        return await formRef.value.confirmDiscardChanges(action);
      }
      return true; // No form ref, proceed
    };

    /**
     * Check if form has unsaved changes
     * @returns {Boolean}
     */
    const hasUnsavedChanges = () => {
      if (formRef.value?.getHasUnsavedChanges) {
        return formRef.value.getHasUnsavedChanges();
      }
      return false;
    };
    onMounted(() => {
      if (panelRef.value) {
        panelRef.value?.focus?.();
      }
    });
    __expose({
      activate,
      formRef,
      saveCharge,
      closePanel,
      handleChargeLoaded,
      confirmDiscardChanges,
      hasUnsavedChanges
    });
    return (_ctx, _cache) => {
      const _component_v_icon = _resolveComponent("v-icon");
      return _openBlock(), _createElementBlock("div", {
        class: "fixed-fee-panel-content h-full flex flex-col",
        tabindex: "0",
        ref_key: "panelRef",
        ref: panelRef
      }, [_createElementVNode("div", _hoisted_1, [_createVNode(CreateChargeForm, {
        ref_key: "formRef",
        ref: formRef,
        state: props.state,
        stateParams: props.stateParams,
        rootScope: props.rootScope,
        alerts: props.alerts,
        snippetService: props.snippetService,
        entryEditService: props.entryEditService,
        tracking: props.tracking,
        clientMatterManager: props.clientMatterManager,
        entryPoint: props.entryPoint,
        isfromVue: true,
        isPanel: true,
        onSaveAndContinue: handleSaveAndContinue,
        onChargeLoaded: handleChargeLoaded,
        onSave: saveCharge,
        onDelete: deleteCharge,
        onClose: closePanel,
        onFixedFeeSaved: handleFixedFeeSaved
      }, null, 8, ["state", "stateParams", "rootScope", "alerts", "snippetService", "entryEditService", "tracking", "clientMatterManager", "entryPoint"])]), _createVNode(LlConfirmationDialog, {
        ref_key: "matterChangeConfirmationRef",
        ref: matterChangeConfirmationRef,
        title: "Confirm Matter Change",
        message: matterChangeMessage.value,
        confirmText: "Yes, Save",
        toggleText: "Cancel",
        showWarningIcon: true,
        "data-cy": "matter-change-confirmation"
      }, null, 8, ["message"]), _createVNode(LlConfirmationDialog, {
        ref_key: "deleteConfirmationRef",
        ref: deleteConfirmationRef,
        title: "Delete Fixed Fee",
        message: "Are you sure you want to delete this fixed fee?",
        confirmText: "Delete",
        toggleText: "Cancel",
        showWarningIcon: true,
        "data-cy": "delete-confirmation"
      }, null, 512), _createVNode(DeleteRecurringFeesDialog, {
        ref_key: "deleteRecurringRef",
        ref: deleteRecurringRef
      }, null, 512), _createElementVNode("div", _hoisted_2, [_createElementVNode("div", _hoisted_3, [_createVNode(LlSecondaryButton, {
        onClick: closePanel,
        disabled: state.isSaving || state.isDeleting,
        "data-cy": "cbff-cancel"
      }, {
        default: _withCtx(() => [...(_cache[0] || (_cache[0] = [_createTextVNode(" Cancel ", -1)]))]),
        _: 1
      }, 8, ["disabled"]), _createElementVNode("div", _hoisted_4, [state.chargeId && !state.isBilled ? (_openBlock(), _createBlock(LlSecondaryButton, {
        key: 0,
        variant: "outlined",
        color: "error",
        onClick: deleteCharge,
        loading: state.isDeleting,
        disabled: state.isSaving,
        "data-cy": "cbff-delete"
      }, {
        default: _withCtx(() => [_createVNode(_component_v_icon, {
          class: "mr-1"
        }, {
          default: _withCtx(() => [...(_cache[1] || (_cache[1] = [_createTextVNode("mdi-delete", -1)]))]),
          _: 1
        }), _cache[2] || (_cache[2] = _createTextVNode(" Delete ", -1))]),
        _: 1
      }, 8, ["loading", "disabled"])) : _createCommentVNode("", true), _createVNode(LlSecondaryButton, {
        onClick: saveCharge,
        loading: state.isSaving,
        disabled: state.isDeleting,
        color: "primary",
        variant: "flat",
        "data-cy": state.chargeId ? 'cbff-save' : 'cbff-create'
      }, {
        default: _withCtx(() => [_createTextVNode(_toDisplayString(state.chargeId ? 'Save' : 'Create') + " Fixed Fee" + _toDisplayString(_unref(fixedFeeTemplate) ? ' Template' : ''), 1)]),
        _: 1
      }, 8, ["loading", "disabled", "data-cy"])])])])], 512);
    };
  }
};